import { injectable, inject } from 'inversify'
import { action, computed, makeObservable } from 'mobx'
import { NavigationRepository } from '@core/infra/navigation/navigation.repository'
import { getParent } from '@core/infra/navigation/navigation-tree.helpers'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import type { RouteId } from '@core/routing/router.config'

interface Breadcrumb {
  id: RouteId
  visibleName: string
}

// What the component actually renders: the crumb plus every display decision
// already resolved to a boolean (machine-architecture.md §3.4).
interface BreadcrumbVm extends Breadcrumb {
  showSeparator: boolean
  showHomeIcon: boolean
  isCurrent: boolean
}

@injectable()
export class BreadcrumbsPresenter {
  @inject(NavigationRepository)
  private navigationRepository!: NavigationRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  constructor() {
    makeObservable(this, {
      viewModel: computed,
      navigate: action,
    })
  }

  get viewModel(): { breadcrumbs: BreadcrumbVm[]; showBreadcrumbs: boolean; navLabel: string } {
    const breadcrumbs = this.getBreadcrumbs()

    return {
      showBreadcrumbs: breadcrumbs.length > 0,
      navLabel: this.i18nRepository.t('shell.breadcrumb'),
      // Defined copy: PM -> VM, property by property (machine-architecture.md §4.4).
      breadcrumbs: breadcrumbs.map((crumb, index) => ({
        id: crumb.id,
        visibleName: crumb.visibleName,
        showSeparator: index > 0,
        showHomeIcon: crumb.id === 'Home',
        isCurrent: index === breadcrumbs.length - 1,
      })),
    }
  }

  private getBreadcrumbs(): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = []
    // routeNode, not currentNode: a breadcrumb trail says where the user *is*.
    // Reading the browse anchor made the trail collapse to the root as soon as
    // the user browsed the menu upward, while still on the same page — the same
    // mistake Up and Section had.
    let currentNode = this.navigationRepository.routeNode

    while (currentNode) {
      // Breadcrumbs are always clickable except the last (the current page) —
      // intentionally independent of the menu's per-item `clickable` flag
      // (e.g. Home is unclickable in the menu but is a normal breadcrumb link).
      if (currentNode.model.routesTo) {
        breadcrumbs.unshift({
          id: currentNode.model.routesTo,
          visibleName: this.i18nRepository.t(currentNode.model.labelKey),
        })
      }
      currentNode = getParent(currentNode)
    }

    return breadcrumbs
  }

  navigate(crumb: Breadcrumb) {
    return this.navigationRepository.navigate(crumb.id)
  }
}
